import React from 'react';
import { Alert, Button, Popconfirm, Space, Typography } from 'antd';
import dayjs from 'dayjs';
import type { ExpV2Test } from '@/types/experimentsV2';
import { CopyableId } from '@/components/CopyableId';
import { actionLabel, canApprove, canReject, describePending } from './actions';

interface Props {
  test: ExpV2Test;
  /** Signed-in admin; undefined until identity loads. */
  adminId: string | undefined;
  canEdit: boolean;
  onApprove: () => void | Promise<void>;
  onReject: () => void | Promise<void>;
}

/** An economy action waits here until a second admin approves it (or anyone rejects it). */
export const PendingApproval: React.FC<Props> = ({
  test,
  adminId,
  canEdit,
  onApprove,
  onReject,
}) => {
  const p = test.pendingAction;
  if (!p) return null;
  const label = actionLabel(p.action);
  const approve = canApprove(test, adminId, canEdit);
  return (
    <Alert
      type="warning"
      showIcon
      data-pending={p.action}
      message={`${label} is waiting for a second admin`}
      description={
        <Space direction="vertical">
          <span>
            Asked by <CopyableId value={p.requestedBy} /> at{' '}
            {dayjs(p.requestedAt).format('D MMM HH:mm')}
          </span>
          {describePending(test, p.payload) && (
            <Typography.Text strong>{describePending(test, p.payload)}</Typography.Text>
          )}
          {adminId === p.requestedBy && (
            <Typography.Text type="secondary">
              You asked for this, so another admin has to approve it.
            </Typography.Text>
          )}
          <Space>
            {approve && (
              <Popconfirm title={`Approve ${label} on ${test.key}?`} onConfirm={onApprove}>
                <Button type="primary" data-approve>
                  Approve
                </Button>
              </Popconfirm>
            )}
            {canReject(test, canEdit) && (
              <Popconfirm title={`Reject ${label} on ${test.key}?`} onConfirm={onReject}>
                <Button danger data-reject>
                  Reject
                </Button>
              </Popconfirm>
            )}
          </Space>
        </Space>
      }
    />
  );
};
